// zustand and middleware imports
import { create } from 'zustand'
import { persist, createJSONStorage, StateStorage } from 'zustand/middleware'

// idb-keyval imports
import { get, set, del } from 'idb-keyval'

// Define the state interface
interface ZudstandState {
  interventions: Intervention[]
  parameters: Parameter[]
  spareparts: Sparepart[]
  technicians: Technician[]
  machinebrands: any[]
  machinemodels: any[]
  machinetypes: any[]
  technicianStock: any[]
  documents: CDocument[]
  technician: Technician | undefined

  setInterventions: (interventions: Intervention[]) => void
  setParameters: (parameters: Parameter[]) => void
  setSpareparts: (spareparts: Sparepart[]) => void
  setTechnicians: (technicians: Technician[]) => void
  setMachinebrands: (machinebrands: any[]) => void
  setMachinemodels: (machinemodels: any[]) => void
  setMachinetypes: (machinetypes: any[]) => void
  setTechnicianStock: (technicianStock: any[]) => void
  setDocuments: (documents: CDocument[]) => void
  setTechnician: (technician: Technician) => void
  reset: () => void
}

// Custom storage using IndexedDB with correct TypeScript annotations
const storage: StateStorage = {
  getItem: async (name: string): Promise<string | null> => {
    return (await get(name)) || null
  },
  setItem: async (name: string, value: string): Promise<void> => {
    await set(name, value)
  },
  removeItem: async (name: string): Promise<void> => {
    await del(name)
  },
}

// Create Zustand store with TypeScript support and persistence in IndexedDB
export const useBoundStore = create<ZudstandState>()(
  persist(
    (set, get) => ({
      interventions: [],
      parameters: [],
      spareparts: [],
      technicians: [],
      machinebrands: [],
      machinemodels: [],
      machinetypes: [],
      technicianStock: [],
      documents: [],
      technician: undefined,
      setInterventions: (interventions: Intervention[]) => set({ interventions }),
      setParameters: (parameters: any[]) => set({ parameters }),
      setSpareparts: (spareparts: Sparepart[]) => set({ spareparts }),
      setTechnicians: (technicians: Technician[]) => set({ technicians }),
      setMachinebrands: (machinebrands: any[]) => set({ machinebrands }),
      setMachinemodels: (machinemodels: any[]) => set({ machinemodels }),
      setMachinetypes: (machinetypes: any[]) => set({ machinetypes }),
      setTechnicianStock: (technicianStock: any[]) => set({ technicianStock }),
      setDocuments: (documents: CDocument[]) => set({ documents }),
      setTechnician: (technician: Technician) => set({ technician }),
      reset: () =>
        set({
          interventions: [],
          parameters: [],
          spareparts: [],
          technicians: [],
          machinebrands: [],
          machinemodels: [],
          machinetypes: [],
          technicianStock: [],
          documents: [],
          technician: undefined,
        }),
    }),

    {
      name: 'init-storage', // name for IndexedDB storage
      storage: createJSONStorage(() => storage), // custom storage configuration
    }
  )
)
